import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DeleteWithReasonDialog from '../components/DeleteWithReasonDialog';
import {
  Archive as ArchiveIcon, Briefcase, Users as UsersIcon, FileSignature, ClipboardList,
  StickyNote, Building2, ShieldAlert, Loader2, RotateCcw, Trash2, Search,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const ENTITY_META = [
  { key: 'jobs',      label: 'Jobs',           icon: Briefcase,    routeHint: '/jobs' },
  { key: 'estimates', label: 'Estimates',      icon: FileSignature, routeHint: '/estimates' },
  { key: 'users',     label: 'Users',          icon: UsersIcon,     routeHint: '/manage-users' },
  { key: 'customers', label: 'Customers',      icon: Building2,     routeHint: '/settings' },
  { key: 'workplans', label: 'Workplans',      icon: ClipboardList, routeHint: '/workplans' },
  { key: 'notes',     label: 'Notes',          icon: StickyNote,    routeHint: '/notes' },
  { key: 'toolbox',   label: 'Toolbox Topics', icon: ShieldAlert,   routeHint: '/reports' },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

function titleFor(entity, item) {
  if (entity === 'users' || entity === 'customers') return item.name || '(no name)';
  if (entity === 'estimates') return item.title || item.estimate_number || '(estimate)';
  return item.title || item.name || '(untitled)';
}

function subtitleFor(entity, item) {
  if (entity === 'jobs') return `${item.job_number ? item.job_number + ' · ' : ''}${item.customer_name || ''}${item.work_order_number ? ' · WO ' + item.work_order_number : ''}${item.city ? ' · ' + item.city : ''}`.trim();
  if (entity === 'estimates') return `${item.estimate_number || ''} · ${item.customer_name || ''}${item.grand_total ? ' · $' + Number(item.grand_total).toFixed(2) : ''}`;
  if (entity === 'users') return `${item.email || ''} · ${item.role || ''}`;
  if (entity === 'customers') return `${item.email || ''}${item.city ? ' · ' + item.city : ''}`;
  if (entity === 'workplans') return item.frequency || '';
  if (entity === 'notes') return (item.body || '').slice(0, 80);
  if (entity === 'toolbox') return (item.description || '').slice(0, 80);
  return '';
}

export default function Archive() {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'admin';
  const isSuperUser = !!user?.is_super_user;
  const canPermanent = isAdmin || isSuperUser;
  const canView = isAdmin || user?.role === 'supervisor';

  const [summary, setSummary] = useState(null);
  const [activeEntity, setActiveEntity] = useState('jobs');
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('q') || ''; } catch { return ''; }
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // { entity, id, name }
  const [restoringId, setRestoringId] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/archive/summary`, { headers: authHeaders });
      setSummary(data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load archive summary', variant: 'destructive' });
    }
  };

  const loadEntity = async (entity, q = '', dfOverride, dtOverride) => {
    const df = dfOverride !== undefined ? dfOverride : dateFrom;
    const dt = dtOverride !== undefined ? dtOverride : dateTo;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/archive/${entity}`, {
        headers: authHeaders,
        params: {
          limit: 500,
          ...(q ? { search: q } : {}),
          ...(df ? { date_from: df } : {}),
          ...(dt ? { date_to: dt } : {}),
        },
      });
      setItems(data.items || []);
      setTotalItems(data.total ?? (data.items || []).length);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || `Failed to load archived ${entity}`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (next) => {
    setActiveEntity(next);
    setSearch('');
    loadEntity(next, '');
  };

  useEffect(() => {
    if (token) {
      loadSummary();
      loadEntity(activeEntity, search);
    }
  }, [token]);

  const handleRestore = async (entity, id) => {
    if (!isAdmin) {
      toast({ title: 'Forbidden', description: 'Only admins can restore items.', variant: 'destructive' });
      return;
    }
    setRestoringId(id);
    try {
      await axios.post(`${API}/archive/${entity}/${id}/restore`, {}, { headers: authHeaders });
      toast({ title: 'Restored', description: `Restored from archive.` });
      await Promise.all([loadSummary(), loadEntity(activeEntity, search)]);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Restore failed', variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async ({ reason }) => {
    if (!confirmDelete) return;
    const { entity, id } = confirmDelete;
    try {
      await axios.delete(`${API}/archive/${entity}/${id}/permanent`, {
        headers: authHeaders,
        params: reason ? { reason } : {},
      });
      toast({ title: 'Permanently deleted', description: 'The item has been erased.' });
      await Promise.all([loadSummary(), loadEntity(activeEntity, search)]);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Permanent delete failed', variant: 'destructive' });
    }
  };

  const counts = summary?.counts || {};
  const total = summary?.total || 0;

  // Workers (role=user) should never reach this page — route them back to
  // their dashboard. This guard lives BELOW every hook so the hook order
  // never changes between renders (React #310 guard, Jun 2026).
  if (user && !canView) {
    return <Navigate to="/worker" replace />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="archive-page">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <ArchiveIcon className="w-7 h-7 text-amber-400" />
            Archive
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Soft-deleted items across HinesTime. Restore mistakes or permanently erase items that aren&apos;t needed.
          </p>
        </div>
        <Badge variant="outline" className="text-amber-400 border-amber-400/40 px-3 py-1 text-sm" data-testid="archive-total-count">
          {total} total archived
        </Badge>
      </div>

      {/* Permissions banner */}
      {!isAdmin && (
        <Card className="mb-4 bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 text-sm text-amber-200">
            You&apos;re viewing the archive in read-only mode. Restore and permanent-delete actions are admin-only.
          </CardContent>
        </Card>
      )}

      <Tabs value={activeEntity} onValueChange={handleTabChange}>
        <TabsList className="bg-[#0F1923] flex flex-wrap h-auto p-1 gap-1" data-testid="archive-tabs">
          {ENTITY_META.map(({ key, label, icon: Icon }) => (
            <TabsTrigger
              key={key}
              value={key}
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center gap-2 px-3 py-1.5"
              data-testid={`archive-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
              {counts[key] > 0 && (
                <Badge className="ml-1 bg-amber-500/20 text-amber-300 border-0 h-5 px-1.5 text-xs" data-testid={`archive-count-${key}`}>
                  {counts[key]}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {ENTITY_META.map(({ key, label }) => (
          <TabsContent key={key} value={key} className="mt-4">
            {/* Search bar */}
            <div className="mb-4 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadEntity(key, search)}
                  placeholder={`Search archived ${label.toLowerCase()}…`}
                  className="pl-9 bg-[#0F1923] border-[#2D3748] text-white"
                  data-testid={`archive-search-${key}`}
                />
              </div>
              <Button
                onClick={() => loadEntity(key, search)}
                disabled={loading}
                data-testid={`archive-search-btn-${key}`}
              >
                Search
              </Button>
            </div>
            {/* Archived-date range filter (Aug 2026 — month-end recovery) */}
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Archived from</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-[#0F1923] border-[#2D3748] text-white w-[160px]"
                  data-testid={`archive-date-from-${key}`}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Archived to</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-[#0F1923] border-[#2D3748] text-white w-[160px]"
                  data-testid={`archive-date-to-${key}`}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => loadEntity(key, search)}
                disabled={loading}
                className="border-[#2D3748] text-slate-300"
                data-testid={`archive-date-apply-${key}`}
              >
                Apply dates
              </Button>
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  onClick={() => { setDateFrom(''); setDateTo(''); loadEntity(key, search, '', ''); }}
                  className="text-slate-400"
                  data-testid={`archive-date-clear-${key}`}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Items list */}
            {loading ? (
              <Card className="bg-[#1A2433]">
                <CardContent className="p-10 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-slate-500 animate-spin mb-2" />
                  <p className="text-slate-500 text-sm">Loading archived {label.toLowerCase()}…</p>
                </CardContent>
              </Card>
            ) : items.length === 0 ? (
              <Card className="bg-[#1A2433] border-2 border-dashed border-[#2D3748]">
                <CardContent className="p-12 text-center">
                  <ArchiveIcon className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400 text-base font-medium">No archived {label.toLowerCase()}</p>
                  <p className="text-slate-500 text-xs mt-1">
                    {(search || dateFrom || dateTo) ? 'No matches for your search or date filter.' : `Soft-deleted ${label.toLowerCase()} will appear here.`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid={`archive-list-${key}`}>
                {(dateFrom || dateTo) && (
                  <p className="text-xs text-sky-300/90 bg-sky-950/40 border border-sky-800/50 rounded-md px-3 py-2" data-testid="archive-date-filter-notice">
                    Date filter active — showing {items.length} item{items.length === 1 ? '' : 's'} archived
                    {dateFrom ? ` from ${dateFrom}` : ''}{dateTo ? ` to ${dateTo}` : ''}. Click Clear to see everything.
                  </p>
                )}
                {totalItems > items.length && (
                  <p className="text-xs text-amber-300/90 bg-amber-950/40 border border-amber-800/50 rounded-md px-3 py-2" data-testid="archive-truncation-notice">
                    Showing the {items.length} most recently archived of {totalItems} total — use search (title, number, customer, work order) to find older items.
                  </p>
                )}
                {items.map((item) => {
                  const archivedAt = item.archived_at || item.deleted_at;
                  const reasonRaw =
                    item.archive_reason ||
                    item.deletion_reason ||
                    null;
                  const deletedBy = item.archived_by_name || item.deleted_by_name || '—';
                  return (
                    <Card key={item.id} className="bg-[#1A2433] border-[#2D3748]" data-testid={`archive-item-${item.id}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate" title={titleFor(key, item)}>
                            {titleFor(key, item)}
                          </p>
                          <p className="text-xs text-slate-400 truncate" title={subtitleFor(key, item)}>
                            {subtitleFor(key, item)}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1.5 text-xs">
                            <span className="text-slate-500">
                              Archived: {formatDate(archivedAt)}
                            </span>
                            <span className="text-slate-500">by {deletedBy}</span>
                            {reasonRaw && (
                              <Badge variant="outline" className="text-amber-300 border-amber-500/40 text-[10px]">
                                {String(reasonRaw).replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {item.archive_trigger && item.archive_trigger !== 'manual_delete' && (
                              <Badge variant="outline" className="text-blue-300 border-blue-500/40 text-[10px]">
                                {String(item.archive_trigger).replace(/_/g, ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestore(key, item.id)}
                              disabled={restoringId === item.id}
                              className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                              data-testid={`archive-restore-${item.id}`}
                            >
                              {restoringId === item.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><RotateCcw className="w-4 h-4 mr-1" /> Restore</>
                              )}
                            </Button>
                          )}
                          {canPermanent && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDelete({ entity: key, id: item.id, name: titleFor(key, item) })}
                              className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                              data-testid={`archive-permanent-delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Delete forever
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Permanent delete confirmation */}
      <DeleteWithReasonDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={`Permanently delete ${confirmDelete?.name || 'this item'}?`}
        description="This will erase the item completely. There is no recovery — even Scalevisory staff cannot restore it after this point."
        entityLabel={confirmDelete?.entity || 'item'}
        onConfirm={handlePermanentDelete}
        dangerLevel="permanent"
      />
    </div>
  );
}
